import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { adminApi } from "../../../../../../../../../services/Api/Admin/adminApi";
import { OrganizationAccessApi } from "../../../../../../../../../services/Api/Admin/adminTypes";
import { OrganizationApi, OrganizationApplicationAccessApi } from "../../../../../../../../../services/Api/types";
import OrganizationSingleAccessForm from "./OrganizationSingleAccessForm";

interface Props {
	organization: OrganizationApi;
	updateAccessesFormData: (data: OrganizationApi) => void;
}

const getFormDefaultValues = (organization: OrganizationApi): OrganizationAccessApi => {
	return {
		accesses: [...(organization.accesses || [])],
		applications: [...(organization.applications || [])].map((app) => ({
			id: app.id,
			accesses: [...(app.accesses || [])],
		})),
	};
};

const OrganizationAccessesForm = ({ organization, updateAccessesFormData }: Props) => {
	const form = useForm<OrganizationAccessApi>({
		criteriaMode: "all",
		defaultValues: getFormDefaultValues(organization),
	});
	const { t } = useTranslation("admin");
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const [accessesLoading, setAccessesLoading] = useState(false);
	const { handleSubmit, reset, setError, formState } = form;

	const getParsedAccesses = (data: OrganizationApplicationAccessApi[]) => {
		return data.map((access) => {
			if (access.custom_value === null) {
				access.custom_value = "";
			}
			delete access.value;
			return access;
		});
	};

	const getParsedDataForm = (data: OrganizationAccessApi) => {
		data.accesses = getParsedAccesses(data.accesses);
		data.applications = data.applications.map((application) => ({
			...application,
			accesses: getParsedAccesses(application.accesses),
		}));

		return data;
	};

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		const newData = getParsedDataForm(data);
		try {
			const params = { include: "applications,applications.accesses,applications.properties,accesses" };
			const res = await adminApi.changeOrganizationAccesses(organization.id, newData, params);
			addSuccessFlash(t("lib:common.flash.saved"));
			updateAccessesFormData(res);
			reset(getFormDefaultValues(res));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const updateAccesses = async () => {
		setAccessesLoading(true);
		const params = { include: "applications,applications.accesses,applications.properties" };
		try {
			const res = await adminApi.updateOrganizationAccesses(Number(organization.id), params);
			updateAccessesFormData(res);
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setAccessesLoading(false);
	};

	return (
		<FormDirty
			formState={formState}
			onSubmit={onSubmit}
			loading={loading}
			buttonSubmitCustom={
				<>
					<ButtonLoading loading={loading} type="submit">
						{t("lib:common.action.save")}
					</ButtonLoading>
					<ButtonLoading loading={accessesLoading} onClick={() => updateAccesses()}>
						{t("modules.organization.action.update_accesses.title")}
					</ButtonLoading>
				</>
			}
		>
			<div className="d-flex flex-column gap-2">
				<OrganizationSingleAccessForm form={form} accesses={organization.accesses} header={organization.name} />
				{organization.applications.map((application, index) => (
					<OrganizationSingleAccessForm
						key={application.id}
						form={form}
						applicationIndex={index}
						header={application.name}
						accesses={application.accesses || []}
					/>
				))}
			</div>
		</FormDirty>
	);
};

export default OrganizationAccessesForm;
